// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { formatDataSources } from './metric-column-formatters.utility';

/**
 * Contributor data model from SQL query (API returns uppercase keys)
 */
export interface ContributorData {
  MEMBER_ID: string;
  MEMBER_DISPLAY_NAME: string;
  MEMBER_LOGO_URL: string;
  LAST_PLATFORM: string;
  LAST_GITHUB_USERNAME: string;
  FIRST_ACTIVITY_TS: string;
  LAST_ACTIVITY_TS: string;
  CONTRIBUTION_COUNT: number;
}

/**
 * Contributor summary data model from SQL query
 */
export interface ContributorSummaryData {
  totalContributors: number;
  dataSources: string;
}

/**
 * Entity service for contributors metric type
 * Handles SQL query: analytics.platinum_organization_dashboard.member_contributions_by_project
 */
@Injectable({
  providedIn: 'root'
})
export class ContributorsMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  /**
   * Get columns for contributors table
   */
  getColumns(): Column[] {
    return [
      { name: 'Our Contributors', prop: 'displayName', clickableText: true },
      { name: 'Username', prop: 'platformValue' },
      // { name: 'Platform', prop: 'platform' },
      { name: 'First activity', prop: 'firstActivity' },
      { name: 'Most recent', prop: 'mostRecent' },
      { name: '# Contributions', prop: 'contributions' }
    ];
  }

  /**
   * Get API endpoint for contributors
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/contributors`;
  }

  /**
   * Fetch contributors data from backend
   * Executes SQL: SELECT member_display_name, last_platform, last_github_username,
   * first_activity_ts, last_activity_ts, contribution_count
   * FROM analytics.platinum_organization_dashboard.member_contributions_by_project
   * WHERE account_id='{account_id}' AND project_slug='{project_slug}'
   * AND time_range_name='last_24mo'
   * ORDER BY contribution_count DESC
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '')
          .set('timeRangeName', params.timeRangeName || 'last_24mo');

        return this.http.get<MetricChartApiResponse<ContributorData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch contributors data');
            }

            return response.data.map(contributor => this.transformToDataRow(contributor));
          }),
          catchError(error => {
            console.error('Error fetching contributors data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in contributors entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform contributor data to DataRow format for the table
   */
  private transformToDataRow(contributor: ContributorData): DataRow {
    const platformValue = this.formatPlatformValue(contributor.LAST_PLATFORM, contributor.LAST_GITHUB_USERNAME);
    const displayNameWithImage = this.formatDisplayNameWithImage(
      contributor.MEMBER_DISPLAY_NAME,
      contributor.MEMBER_LOGO_URL
    );

    return {
      memberId: { text: contributor.MEMBER_ID },
      displayName: {
        ...displayNameWithImage,
        extraData: {
          memberId: contributor.MEMBER_ID,
          memberDisplayName: contributor.MEMBER_DISPLAY_NAME,
          memberLogoUrl: contributor.MEMBER_LOGO_URL
        }
      },
      platform: { text: contributor.LAST_PLATFORM },
      platformValue: platformValue,
      firstActivity: { text: this.formatDate(contributor.FIRST_ACTIVITY_TS) },
      mostRecent: { text: this.formatDate(contributor.LAST_ACTIVITY_TS) },
      contributions: { text: contributor.CONTRIBUTION_COUNT.toLocaleString('en-US') }
    };
  }

  /**
   * Format display name with circular image
   */
  private formatDisplayNameWithImage(
    displayName: string,
    logoUrl: string
  ): { text: string; isHtml: boolean } {
    const imageHtml = logoUrl
      ? `<img src="${logoUrl}" alt="${displayName}" class="maintainer-avatar" onerror="this.style.display='none'">`
      : '';

    return {
      text: `<div class="maintainer-profile">${imageHtml}<span class="maintainer-name">${displayName}</span></div>`,
      isHtml: true
    };
  }

  /**
   * Format platform value with appropriate prefix and icons
   */
  private formatPlatformValue(platform: string, platformValue: string): { text: string; isHtml?: boolean } {
    if (platform.toLowerCase() === 'github') {
      return {
        text: `<i class="fab fa-github" style="margin-right: 4px;"></i> &nbsp;@${platformValue}`,
        isHtml: true
      };
    }

    return { text: platformValue };
  }

  /**
   * Fetch contributors summary data from backend
   * Executes SQL: SELECT COUNT(DISTINCT member_id) total_contributors,
   * LISTAGG(DISTINCT last_platform, ' , ') data_sources
   * FROM analytics.platinum_organization_dashboard.member_contributions_by_project
   * WHERE project_slug='{project_slug}' AND time_range_name='last_24mo'
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '')
          .set('timeRangeName', params.timeRangeName || 'last_24mo');

        return this.http.get<{success: boolean; message?: string; data: ContributorSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch contributors summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = [
              { name: 'Definition', prop: 'definition' },
              { name: 'Total count for this project', prop: 'totalCount' },
              { name: 'Data source', prop: 'dataSource' }
            ];

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: 'Any individual who makes a contribution is considered to be a contributor. We define a contribution in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.', isHtml: true },
                totalCount: { text: response.data.totalContributors.toLocaleString('en-US')  },
                dataSource: { text: formatDataSources(response.data.dataSources) }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching contributors summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = [
              { name: 'Definition', prop: 'definition' },
              { name: 'Total count for this project', prop: 'totalCount' },
              { name: 'Data source', prop: 'dataSource' }
            ];
            const fallbackData: DataRow[] = [
              {
                definition: { text: 'Any individual who makes a contribution is considered to be a contributor. We define a contribution in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.', isHtml: true },
                totalCount: { text: 'N/A' },
                dataSource: { text: 'N/A' }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in contributors summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = [
          { name: 'Definition', prop: 'definition' },
          { name: 'Total count for this project', prop: 'totalCount' },
          { name: 'Data source', prop: 'dataSource' }
        ];
        const fallbackData: DataRow[] = [
          {
            definition: { text: 'Any individual who makes a contribution is considered to be a contributor. We define a contribution in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.', isHtml: true },
            totalCount: { text: 'N/A' },
            dataSource: { text: 'N/A' }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}
